import { useAuth } from '@/lib/auth/react';
import { BYPASS_AUTH_MOCK_USER, useAppUser } from './useAppUser';
import { isOnboardingComplete } from './onboarding';

/**
 * Dev-only override: when `VITE_BYPASS_AUTH=true`, force the app to behave
 * as if the user is authenticated. Unblocks protected routes and skips the
 * initial auth-loading splash so devs can navigate the app without signing in.
 *
 * Note: this only affects UI auth gates. Network calls that require a real
 * access token (Orval-generated hooks, etc.) will still 401 — the bypass does
 * not mint a fake token. Use a logged-in session for any flow that hits the api.
 */

const BYPASS_AUTH = import.meta.env.VITE_BYPASS_AUTH === 'true';

/**
 * Drop-in replacement for `useAuth()` from `@/lib/auth/react`
 * that respects the `VITE_BYPASS_AUTH` dev flag. Use this anywhere the UI gates
 * on auth state (route guards, conditional nav). Continue using `useAuth()`
 * directly when you need real auth methods (`signIn*`, `getAuthHeaders`, etc.).
 *
 * Two levels of "logged in" (ARC-145 model — a registered account experiences
 * the app as fully authenticated; only *actions* resume the unfinished funnel):
 *  - `isAuthenticated` — a registered account with a live session, even if
 *    onboarding (email verification, terms) isn't finished. Drives the whole
 *    authenticated experience: top bar, shell, navigation, protected routes.
 *  - `isOnboarded` — session AND every onboarding step complete
 *    (`isOnboardingComplete`). Gates authenticated *post* actions (deposit,
 *    trading entry points): a not-yet-onboarded user attempting one is routed
 *    back into the signup funnel at their next step instead.
 *
 * Onboarding state is read from the live `/auth/me`-overlaid `useAppUser()`
 * profile (not the raw, possibly-stale token claims), so a refresh reflects the
 * server's current verification state.
 */
export function useAppAuth() {
  const auth = useAuth();
  const user = useAppUser();
  if (BYPASS_AUTH) {
    return {
      ...auth,
      isAuthenticated: true,
      isOnboarded: true,
      isLoading: false,
      user: BYPASS_AUTH_MOCK_USER,
    };
  }
  return {
    ...auth,
    user,
    isAuthenticated: auth.isAuthenticated,
    isOnboarded: auth.isAuthenticated && isOnboardingComplete(user),
  };
}
